import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, ArrowLeft, KeyRound, Copy, CheckCircle2 } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {message, reset_url?}

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Enter a valid email");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <PublicNav />
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          {result ? (
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8 text-center" data-testid="forgot-success">
              <div className="w-14 h-14 rounded-full bg-brand-greenSoft grid place-items-center mx-auto">
                <CheckCircle2 size={28} className="text-brand-greenDark" />
              </div>
              <h2 className="mt-4 font-heading font-semibold text-xl text-brand-navy">
                Check your inbox
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                {result.message}
              </p>
              <p className="mt-3 text-xs text-slate-500">
                The reset link expires in 30 minutes. If it hasn't arrived in a few
                minutes, check your spam folder or contact SchoolLane support.
              </p>
              <Link to="/login" data-testid="forgot-back-login"
                className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-brand-blue hover:text-brand-blueDark">
                <ArrowLeft size={13} /> Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8" data-testid="forgot-form">
              <div className="w-11 h-11 rounded-lg bg-brand-blueSoft text-brand-blue grid place-items-center">
                <KeyRound size={20} />
              </div>
              <h1 className="mt-4 font-heading font-semibold text-2xl text-brand-navy">Forgot your password?</h1>
              <p className="mt-2 text-sm text-slate-600">
                Enter your email and we'll send you a link to reset it. Link is valid for 30 minutes.
              </p>
              <label className="mt-6 block text-xs font-semibold text-slate-600">Email address</label>
              <div className="relative mt-1">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input required type="email" data-testid="forgot-email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@school.edu.in"
                  className="pl-9 pr-4 py-3 w-full rounded-md border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-brand-blue" />
              </div>
              <button type="submit" disabled={submitting} data-testid="forgot-submit"
                className="mt-6 w-full py-3 rounded-md bg-brand-blue text-white font-semibold hover:bg-brand-blueDark disabled:opacity-60">
                {submitting ? "Sending…" : "Send reset link"}
              </button>
              <Link to="/login" data-testid="forgot-back-login"
                className="mt-4 flex items-center justify-center gap-1 text-sm text-slate-600 hover:text-brand-navy">
                <ArrowLeft size={13} /> Back to sign in
              </Link>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
